import {
  APIEndpoint,
  type TokenVerificationRequest,
  type UserSettings,
  type Snippet,
  type CreateSnippetData,
  type UpdateSnippetData,
  type BillingData,
  type CreateSupportTicketData,
  type CreateSupportFeedbackData,
  type TopupPurchaseHistory,
  type FeatureFlagsResponse,
  type ToggleFeatureFlagRequest,
} from "@/types/auth";
import { fetchWithErrorHandlers } from "./fetchWithErrorHandlers";
import { FillAppError } from "./FillAppError";
import { auth } from "./firebase";

/**
 * API configuration
 */
class APIConfig {
  private static instance: APIConfig;
  private baseURL: string;

  private constructor() {
    this.baseURL = process.env.NEXT_PUBLIC_API_URL || "";
    if (!this.baseURL) {
      throw new Error("NEXT_PUBLIC_API_URL environment variable is required");
    }
  }

  public static getInstance(): APIConfig {
    if (!APIConfig.instance) {
      APIConfig.instance = new APIConfig();
    }
    return APIConfig.instance;
  }

  public getBaseURL(): string {
    return this.baseURL;
  }

  public getEndpointURL(endpoint: APIEndpoint): string {
    return `${this.baseURL}${endpoint}`;
  }
}

/**
 * Get authentication headers for API requests
 */
export const getAuthHeaders = async (
  includeContentType = true,
  forceRefresh = false
) => {
  const user = auth.currentUser;
  if (!user) {
    throw new FillAppError("unauthorized:auth", "User not authenticated");
  }
  const token = await user.getIdToken(forceRefresh);
  return {
    Authorization: `Bearer ${token}`,
    ...(includeContentType && { "Content-Type": "application/json" }),
  };
};

/**
 * Authentication API functions
 */
export class AuthAPI {
  /**
   * Verify Firebase ID token and get custom token
   */
  private config: APIConfig;

  constructor() {
    this.config = APIConfig.getInstance();
  }

  public async verifyToken(idToken: string): Promise<string> {
    try {
      const request: TokenVerificationRequest = { idToken };
      const response = await fetchWithErrorHandlers(
        this.config.getEndpointURL(APIEndpoint.VERIFY_TOKEN),
        {
          method: "POST",
          body: JSON.stringify(request),
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (!data.customToken) {
        throw new FillAppError(
          "bad_request:auth",
          "Invalid response: customToken is missing"
        );
      }

      return data.customToken;
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Token verification failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error("Token verification error:", error);
      throw new FillAppError(
        "internal_server:auth",
        `Token verification failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }
}

/**
 * User Settings API functions
 */
export class SettingsAPI {
  /**
   * Get user settings
   */
  static async getUserSettings(): Promise<UserSettings> {
    try {
      const headers = await getAuthHeaders();
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.USER_SETTINGS);

      const response = await fetchWithErrorHandlers(url, {
        headers,
      });

      const { data } = await response.json();

      return data as UserSettings;
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Get user settings failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error("Get user settings error:", error);
      throw new FillAppError(
        "internal_server:user",
        `Failed to get user settings: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * Update user settings
   */
  static async updateUserSettings(
    updates: Partial<UserSettings>
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders(true);
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.USER_SETTINGS);

      await fetchWithErrorHandlers(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(updates),
      });
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Update user settings failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error("Update user settings error:", error);
      throw new FillAppError(
        "internal_server:user",
        `Failed to update user settings: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }
}

/**
 * Account API functions
 */
export class AccountAPI {
  /**
   * Delete user account
   */
  static async deleteAccount(): Promise<void> {
    try {
      const headers = await getAuthHeaders(false);
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.DELETE_ACCOUNT);

      await fetchWithErrorHandlers(url, {
        method: "DELETE",
        headers,
      });
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Delete account failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error("Delete account error:", error);
      throw new FillAppError(
        "internal_server:user",
        `Failed to delete account: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }
}

/**
 * Billing API functions
 */
export const fetchBillingData = async (): Promise<BillingData> => {
  const headers = await getAuthHeaders();
  const config = APIConfig.getInstance();
  const response = await fetchWithErrorHandlers(
    config.getEndpointURL(APIEndpoint.BILLING),
    {
      headers,
    }
  );
  const data = await response.json();
  return data.data;
};

/**
 * Get billing portal URL for subscription management
 */
export const getBillingPortalUrl = async (): Promise<string> => {
  const headers = await getAuthHeaders(false);
  const config = APIConfig.getInstance();
  const response = await fetchWithErrorHandlers(
    `${config.getBaseURL()}/subscription/billing-portal`,
    {
      method: "GET",
      headers,
    }
  );
  const { data } = await response.json();
  return data.portalUrl;
};

/**
 * Create checkout session for subscription
 */
export const createCheckoutSession = async (lookupKey: string): Promise<string> => {
  const headers = await getAuthHeaders(true);
  const config = APIConfig.getInstance();
  const response = await fetchWithErrorHandlers(
    `${config.getBaseURL()}/subscription/checkout`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ lookupKey }),
    }
  );
  const { data } = await response.json();
  return data.checkoutUrl;
};

/**
 * Verify subscription session after successful checkout
 */
export const verifySubscriptionSession = async (sessionId: string): Promise<void> => {
  const headers = await getAuthHeaders(true);
  const config = APIConfig.getInstance();
  await fetchWithErrorHandlers(
    `${config.getBaseURL()}/subscription/verify-session`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ sessionId }),
    }
  );
};


/**
 * Snippets API functions
 */
export class SnippetsAPI {
  /**
   * Get all user snippets
   */
  static async fetchSnippets(): Promise<Snippet[]> {
    const headers = await getAuthHeaders();
    const config = APIConfig.getInstance();
    const url = config.getEndpointURL(APIEndpoint.SNIPPETS);
    const response = await fetchWithErrorHandlers(url, {
      headers,
    });
    const data = await response.json();
    return data.data || data;
  }

  /**
   * Create a new snippet
   */
  static async createSnippet(data: CreateSnippetData): Promise<Snippet> {
    const headers = await getAuthHeaders(true);
    const config = APIConfig.getInstance();
    const url = config.getEndpointURL(APIEndpoint.SNIPPETS);

    const response = await fetchWithErrorHandlers(url, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    return await response.json();
  }

  /**
   * Update an existing snippet
   */
  static async updateSnippet(
    id: string,
    updates: UpdateSnippetData
  ): Promise<Snippet> {
    const headers = await getAuthHeaders(true);
    const config = APIConfig.getInstance();
    const url = `${config.getEndpointURL(APIEndpoint.SNIPPETS)}/${id}`;

    const response = await fetchWithErrorHandlers(url, {
      method: "PUT",
      headers,
      body: JSON.stringify(updates),
    });

    return response.json();
  }

  /**
   * Delete a snippet
   */
  static async deleteSnippet(id: string): Promise<void> {
    const headers = await getAuthHeaders(false);
    const config = APIConfig.getInstance();
    const url = `${config.getEndpointURL(APIEndpoint.SNIPPETS)}/${id}`;

    await fetchWithErrorHandlers(url, {
      method: "DELETE",
      headers,
    });
  }
}

/**
 * Support API functions
 */
export class SupportAPI {
  /**
   * Create a new support ticket
   */
  static async createSupportTicket(
    data: CreateSupportTicketData
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders(true);
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.SUPPORT_TICKETS);

      await fetchWithErrorHandlers(url, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Create support ticket failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error("Create support ticket error:", error);
      throw new FillAppError(
        "internal_server:api",
        `Failed to create support ticket: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * Submit feedback (no authentication required)
   */
  static async submitFeedback(data: CreateSupportFeedbackData): Promise<void> {
    const config = APIConfig.getInstance();
    const url = config.getEndpointURL(APIEndpoint.SUPPORT_FEEDBACK);

    await fetchWithErrorHandlers(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }
}

/**
 * Topup API functions
 */
export class TopupAPI {
  /**
   * Create top-up checkout session
   */
  static async createTopupCheckout(usdAmount: number): Promise<{ checkoutUrl: string; sessionId: string; credits: number; usdAmount: number }> {
    try {
      const headers = await getAuthHeaders(true);
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.TOPUP_CHECKOUT);

      const response = await fetchWithErrorHandlers(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ usdAmount }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Create top-up checkout failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error('Create top-up checkout error:', error);
      throw new FillAppError(
        'internal_server:api',
        `Failed to create top-up checkout: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * Get top-up purchase history
   */
  static async getTopupHistory(): Promise<TopupPurchaseHistory[]> {
    try {
      const headers = await getAuthHeaders(false);
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.TOPUP_HISTORY);

      const response = await fetchWithErrorHandlers(url, {
        headers,
      });

      const data = await response.json();
      return data.purchases;
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Get top-up history failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error('Get top-up history error:', error);
      throw new FillAppError(
        'internal_server:api',
        `Failed to get top-up history: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * Verify top-up session after successful checkout
   */
  static async verifyTopupSession(sessionId: string): Promise<void> {
    const headers = await getAuthHeaders(true);
    const config = APIConfig.getInstance();
    await fetchWithErrorHandlers(
      `${config.getBaseURL()}/topup/verify-session`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ sessionId }),
      }
    );
  }
}


/**
 * Feature Flags API functions
 */
export class FeatureFlagsAPI {
  /**
   * Get all feature flags with user's enabled status
   */
  static async getFeatureFlags(): Promise<FeatureFlagsResponse> {
    try {
      const headers = await getAuthHeaders(false);
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.FEATURE_FLAGS);

      const response = await fetchWithErrorHandlers(url, {
        headers,
      });

      return await response.json();
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Get feature flags failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error("Get feature flags error:", error);
      throw new FillAppError(
        "internal_server:api",
        `Failed to get feature flags: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * Toggle a specific feature flag
   */
  static async toggleFeatureFlag(
    request: ToggleFeatureFlagRequest
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders(true);
      const config = APIConfig.getInstance();
      const url = config.getEndpointURL(APIEndpoint.FEATURE_FLAGS_TOGGLE);

      await fetchWithErrorHandlers(url, {
        method: "POST",
        headers,
        body: JSON.stringify(request),
      });
    } catch (error) {
      if (error instanceof FillAppError) {
        console.error(
          `Toggle feature flag failed [${error.statusCode}]:`,
          error.message
        );
        throw error;
      }

      console.error("Toggle feature flag error:", error);
      throw new FillAppError(
        "internal_server:api",
        `Failed to toggle feature flag: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }
}

export interface SubscriptionPlanChange {
  plan: string;
  portalUrl?: string;
  effectiveAt?: number;
}

export async function changeSubscriptionPlan(lookupKey: string): Promise<SubscriptionPlanChange> {
  const config = APIConfig.getInstance();
  const response = await fetchWithErrorHandlers(`${config.getBaseURL()}/subscription/change-plan`, {
    method: 'POST', headers: await getAuthHeaders(true), body: JSON.stringify({ lookupKey }),
  });
  return (await response.json()).data;
}

export async function getScheduledPlanChange(): Promise<SubscriptionPlanChange | null> {
  const config = APIConfig.getInstance();
  const response = await fetchWithErrorHandlers(`${config.getBaseURL()}/subscription/scheduled-change`, {
    headers: await getAuthHeaders(false),
  });
  return (await response.json()).data;
}

export async function cancelScheduledPlanChange(): Promise<void> {
  const config = APIConfig.getInstance();
  await fetchWithErrorHandlers(`${config.getBaseURL()}/subscription/scheduled-change`, {
    method: 'DELETE', headers: await getAuthHeaders(false),
  });
}
